// live-map.jsx — React wrapper around the main-thread OSM tile renderer (TileMap)
const { useRef: useR, useEffect: useE } = React;

function LiveMapPoster(props) {
  const { location, theme, markerStyle, detail, paper, labels, titleFont, grain, apiRef } = props;
  const wrapRef = useR(null);
  const canvasRef = useR(null);
  const tmRef = useR(null);
  const drag = useR(null);
  const wheelT = useR(0);
  const lastZoom = useR(location.zoom || 13);
  const p = useR(props);
  p.current = props;

  // init the tile map once
  useE(() => {
    const tm = new window.TileMap({
      canvas: canvasRef.current,
      theme, detail, markerStyle,
      lng: location.lng, lat: location.lat, zoom: location.zoom || 13,
      onView: (view) => {
        const cb = p.current.onZoom;
        if (cb && view.zoom !== lastZoom.current) { lastZoom.current = view.zoom; cb(view.zoom); }
      },
    });
    tmRef.current = tm;

    const ro = new ResizeObserver(() => tm.resize());
    if (wrapRef.current) ro.observe(wrapRef.current);

    if (apiRef) apiRef.current = {
      recenter: () => { const pr = p.current; tm.setPin(pr.location.lng, pr.location.lat); tm.setView({ lng: pr.location.lng, lat: pr.location.lat, zoom: pr.location.zoom || 13 }); },
      zoomIn: () => { tm.setDragging(true); tm.setView({ zoom: window.TileMapHelpers.clamp(tm.view.zoom + 0.6, 3, 17) }); clearTimeout(wheelT.current); wheelT.current = setTimeout(() => tm.setDragging(false), 220); },
      zoomOut: () => { tm.setDragging(true); tm.setView({ zoom: window.TileMapHelpers.clamp(tm.view.zoom - 0.6, 3, 17) }); clearTimeout(wheelT.current); wheelT.current = setTimeout(() => tm.setDragging(false), 220); },
      capture: async () => {
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 2500))]);
        // Warm-up pass: renders the poster framing so any tiles the taller
        // portrait/landscape crop needs (beyond the live view) get requested,
        // then wait for them before the real capture so the preview isn't blank.
        capturePoster(tm, p.current, 760);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 3500))]);
        await new Promise((r) => setTimeout(r, 30));
        return capturePoster(tm, p.current, 760);
      },
      // Exact framing so the order is a deterministic reproduction of what the
      // customer saw — used to regenerate the print file at any resolution.
      getView: () => ({
        lng: tm.view.lng, lat: tm.view.lat, zoom: tm.view.zoom,
        // pin (marker) coords + live map width — let the server reproduce the
        // exact framing/marker when it re-renders the print file from _print_spec.
        pin: tm.pin ? { lng: tm.pin.lng, lat: tm.pin.lat } : null,
        cssW: (tm.canvas && tm.canvas.clientWidth) || 0,
        cssH: (tm.canvas && tm.canvas.clientHeight) || 0,
      }),
      // High-resolution print render. longEdgePx is sized from the chosen paper
      // at 300 DPI (capped in-browser; a server can re-render at full size from
      // getView()). Returns a PNG data URL.
      capturePrint: async (longEdgePx, fullRes) => {
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 4000))]);
        // Warm tiles for the poster framing (same tiles at any scale — tile zoom
        // depends only on view zoom), then wait before the high-res capture.
        capturePoster(tm, p.current, 760);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 5000))]);
        // Second warm-up at PRINT scale: satellite mode fetches higher-zoom
        // imagery for sharp prints, and those tiles are only requested at this
        // scale — without this pass the aerial export captures before any photo
        // tiles arrive and comes out blank. (No-op extra pass for vector maps.)
        capturePoster(tm, p.current, longEdgePx, fullRes);
        await Promise.race([tm.idle(), new Promise((r) => setTimeout(r, 20000))]);
        await new Promise((r) => setTimeout(r, 30));
        return capturePoster(tm, p.current, longEdgePx, fullRes);
      },
    };

    return () => { ro.disconnect(); if (tm._raf) cancelAnimationFrame(tm._raf); tmRef.current = null; };
  }, []);

  // search -> fly to new location
  useE(() => {
    const tm = tmRef.current; if (!tm) return;
    tm.setPin(location.lng, location.lat);
    tm.setView({ lng: location.lng, lat: location.lat, zoom: location.zoom || 13 });
  }, [location.lng, location.lat, location.zoom]);

  useE(() => { const tm = tmRef.current; if (tm) tm.setTheme(theme, detail); }, [theme, detail]);
  useE(() => { const tm = tmRef.current; if (tm) tm.setMarker(markerStyle); }, [markerStyle]);
  useE(() => { const tm = tmRef.current; if (tm) requestAnimationFrame(() => tm.resize()); }, [paper.aspect, paper.landscape]);

  // pan & zoom — or drag the marker pin if you grab it
  const onDown = (e) => {
    const tm = tmRef.current; if (!tm) return;
    const r = canvasRef.current.getBoundingClientRect();
    if (tm.hitPin(e.clientX - r.left, e.clientY - r.top)) {
      drag.current = { pin: true, rect: r };
      canvasRef.current.style.cursor = "grabbing";
    } else {
      tm.setDragging(true);
      canvasRef.current.style.cursor = "grabbing";
      drag.current = { x: e.clientX, y: e.clientY, lng: tm.view.lng, lat: tm.view.lat, zoom: tm.view.zoom };
    }
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch (err) {}
  };
  const onMove = (e) => {
    const d = drag.current, tm = tmRef.current; if (!tm) return;
    if (d && d.pin) {
      const r = d.rect;
      const ll = tm.screenToLngLat(e.clientX - r.left, e.clientY - r.top);
      if (ll) tm.setPin(ll.lng, ll.lat);
      return;
    }
    if (!d) {
      // hover feedback: show a grab cursor when over the pin
      const r = canvasRef.current.getBoundingClientRect();
      canvasRef.current.style.cursor = tm.hitPin(e.clientX - r.left, e.clientY - r.top) ? "move" : "grab";
      return;
    }
    const H = window.TileMapHelpers;
    const ws = 256 * Math.pow(2, d.zoom);
    const wx = H.lon2x(d.lng, ws) - (e.clientX - d.x);
    const wy = H.lat2y(d.lat, ws) - (e.clientY - d.y);
    tm.setView({ lng: H.x2lon(wx, ws), lat: H.y2lat(H.clamp(wy, 1, ws - 1), ws) });
  };
  const onUp = (e) => {
    const c = canvasRef.current;
    if (c && c.hasPointerCapture && c.hasPointerCapture(e.pointerId)) c.releasePointerCapture(e.pointerId);
    const tm = tmRef.current; if (tm) tm.setDragging(false);
    if (c) c.style.cursor = "grab";
    drag.current = null;
  };
  const onWheel = (e) => {
    e.preventDefault(); const tm = tmRef.current; if (!tm) return;
    tm.setDragging(true);   // lighter detail while the wheel is spinning
    tm.setView({ zoom: window.TileMapHelpers.clamp(tm.view.zoom + (e.deltaY < 0 ? 0.5 : -0.5), 3, 17) });
    clearTimeout(wheelT.current);
    wheelT.current = setTimeout(() => tm.setDragging(false), 200);   // full detail once it stops
  };

  const e = React.createElement;
  return e("div", { className: "live-poster", style: { width: "100%", height: "100%", display: "flex", flexDirection: "column", background: theme.bg, position: "relative", overflow: "hidden", containerType: "size" } }, [
    e("div", { key: "map", ref: wrapRef, style: { flex: "1 1 auto", minHeight: 0, position: "relative" } },
      e("canvas", {
        ref: canvasRef, onPointerDown: onDown, onPointerMove: onMove, onPointerUp: onUp, onPointerCancel: onUp, onWheel,
        style: { width: "100%", height: "100%", display: "block", cursor: drag.current ? "grabbing" : "grab", touchAction: "none" },
      })
    ),
    grain ? e("div", { key: "gr", style: { position: "absolute", inset: 0, pointerEvents: "none", mixBlendMode: "multiply", opacity: 0.05, backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" } }) : null,
    labels.show ? e("div", { key: "cap", style: { flex: "0 0 auto", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "5.5% 6% 6.5%", gap: "0.5em", color: theme.text, position: "relative", zIndex: 2 } }, [
      labels.subtitleTop ? e("div", { key: "rule", style: { display: "flex", alignItems: "center", gap: "0.9em", width: "62%", color: theme.major } }, [
        e("span", { key: "l", style: { flex: 1, height: 1, background: "currentColor", opacity: 0.6 } }),
        e("span", { key: "d", style: { width: 5, height: 5, borderRadius: "50%", background: "currentColor" } }),
        e("span", { key: "r", style: { flex: 1, height: 1, background: "currentColor", opacity: 0.6 } }),
      ]) : null,
      e("div", { key: "t", style: { fontFamily: titleFont, fontWeight: 600, letterSpacing: labels.spaced ? "0.12em" : "0.02em", textTransform: labels.upper ? "uppercase" : "none", fontSize: "clamp(13px, " + (paper.landscape ? 5 : 5.4) + "cqh, 72px)", lineHeight: 1.04 } }, labels.title || "Your Place"),
      labels.subtitle ? e("div", { key: "s", style: { fontSize: "clamp(7px,1.9cqh,20px)", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.78 } }, labels.subtitle) : null,
      labels.showCoords && labels.coords ? e("div", { key: "c", style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "clamp(6px,1.5cqh,13px)", letterSpacing: "0.18em", opacity: 0.62, marginTop: "0.55em" } }, labels.coords) : null,
    ]) : null,
  ]);
}

// render the poster CONTENT (map + caption, no border) into a W×H canvas.
function renderPosterContent(tm, pr, W, H) {
  // preserve the SAME framing the customer sees: span = liveCssW / 2^(zoom-tz),
  // so render scale = (content map width) / liveCssW.
  const canvas = tm.canvas;
  const liveCssW = (canvas && (canvas.clientWidth || canvas.width / (tm.dpr || 1))) || W;
  const liveCssH = (canvas && (canvas.clientHeight || canvas.height / (tm.dpr || 1))) || Math.round(H * 0.8);
  const renderScale = W / liveCssW;
  // Map area height: reproduce the EXACT on-screen crop by giving the print map
  // the SAME aspect as the live map canvas (liveCssH/liveCssW). The on-screen
  // caption band is content-driven (1 vs 2 lines, coords on/off, font, size), so
  // a fixed band % (the old 0.18/0.2) never matched and the print showed more/
  // less of the map than the customer framed. Derive it from the live canvas
  // instead; the caption then fills whatever height remains, exactly like the
  // flex layout on screen.
  let mapH = pr.labels.show ? Math.round(W * (liveCssH / liveCssW)) : H;
  mapH = Math.max(1, Math.min(mapH, H));
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = pr.theme.bg; ctx.fillRect(0, 0, W, H);
  const tmp = document.createElement("canvas");
  tmp.width = W; tmp.height = mapH;
  tm.render({ ctx: tmp.getContext("2d"), w: W, h: mapH, scale: renderScale, view: tm.view });
  ctx.drawImage(tmp, 0, 0);
  if (pr.labels.show && window.drawCaption && H - mapH > 0) window.drawCaption(ctx, pr.labels, pr.theme, pr.titleFont, W, mapH, H - mapH);
  return cv;
}

// render the current map + caption (and the chosen Border) to a PNG data URL.
// targetW = output width on the SHORT edge of a portrait poster (the height
// follows the paper aspect). Used for the cart preview (~760) and the high-res
// print file (300 DPI). The "Border" choice (mat / fine line / none) is baked in
// so the print shows the same margin the customer framed on screen.
function capturePoster(tm, pr, targetW, fullRes) {
  const aspect = pr.paper.aspect;
  // Customer/cart renders stay capped (~3800px) so they're fast and safe on any
  // device. The admin Studio export passes fullRes to lift the ceiling and
  // produce true print pixels (e.g. 70×100cm @ 300 DPI = 8268×11811).
  const SAFE = fullRes ? 12000 : 3800;
  const longPerShort = aspect >= 1 ? aspect : 1 / aspect;
  let baseW = targetW <= 900 ? targetW : Math.min(targetW, fullRes ? 9000 : 4200);
  if (baseW * longPerShort > SAFE) baseW = Math.floor(SAFE / longPerShort);
  const totalH = Math.round(baseW * aspect);

  const border = pr.frameMat || "none";
  const mn = Math.min(baseW, totalH);
  // Cream border widths as a fraction of the short edge, matched to the on-screen
  // frameStyle() so the print reproduces what the customer framed: a wide mat, or
  // a thin cream edge with a keyline at the map's edge.
  const inset = border === "mat" ? Math.round(mn * 0.065) : border === "line" ? Math.round(mn * 0.028) : 0;

  const content = renderPosterContent(tm, pr, baseW - inset * 2, totalH - inset * 2);

  const cv = document.createElement("canvas");
  cv.width = baseW; cv.height = totalH;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = border === "none" ? pr.theme.bg : "#fbf8f1";
  ctx.fillRect(0, 0, baseW, totalH);
  ctx.drawImage(content, inset, inset);
  if (border === "line") {
    // Keyline HUGS the map content (drawn at the content edge), with the cream
    // border outside it — exactly like the on-screen outline (outlineOffset:-8px
    // sitting at the map edge). Previously it was drawn at 2.2% while the content
    // sat at 4.5%, leaving the fine-line frame floating in the margin.
    ctx.strokeStyle = "rgba(48,38,24,.55)";
    ctx.lineWidth = Math.max(1, Math.round(baseW * 0.0016));
    ctx.strokeRect(inset, inset, baseW - inset * 2, totalH - inset * 2);
  }
  // Large canvases as JPEG (a full-res PNG data URL can be 100MB+ and crash the
  // tab); smaller previews stay PNG. The Studio exporter re-tags DPI either way.
  const big = fullRes || baseW * totalH > 20e6;
  try { return cv.toDataURL(big ? "image/jpeg" : "image/png", big ? 0.92 : undefined); } catch (e) { return ""; }
}

window.LiveMapPoster = LiveMapPoster;
